import './HvacFormGroupArea.scss';
import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import { FieldPath } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { memo, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { CoolingIcon } from 'src/modules/common/icons/CoolingIcon';
import { FanIcon } from 'src/modules/common/icons/FanIcon';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { VisibilityIcon } from 'src/modules/common/icons/VisibilityIcon';
import { VisibilityOffIcon } from 'src/modules/common/icons/VisibilityOffIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { FormError } from 'src/modules/form/types/FormError';
import { modifyField } from 'src/modules/form/utils/field';
import { HvacConfig } from 'src/modules/hvac/types/HvacConfig';
import { HvacFormData, HvacFormGroupAreaData, HvacFormSectionData } from 'src/modules/hvac/types/HvacFormData';
import { getDefaultFormSection } from 'src/modules/hvac/utils/getDefaultFormSection';
import { HvacFormAreaCommentFormModal } from 'src/modules/hvac/views/HvacFormAreaCommentFormModal';
import { HvacFormAreaResetConfirmationModal } from 'src/modules/hvac/views/HvacFormAreaResetConfirmationModal';
import { HvacFormSection } from 'src/modules/hvac/views/HvacFormSection';

type Props = {
  readonly group: HvacGroup;
  readonly area: HvacArea;
  readonly field: FieldPath<HvacFormData, FormError, HvacFormGroupAreaData>;
  readonly config: HvacConfig;
  readonly openedSection: FieldPath<HvacFormData, FormError, HvacFormSectionData> | null;
  readonly readOnly: boolean;
  readonly setOpenedSection: (section: FieldPath<HvacFormData, FormError, HvacFormSectionData> | null) => void;
};

export const HvacFormGroupArea = memo(({
  group,
  area,
  config,
  field,
  openedSection,
  readOnly,
  setOpenedSection,
}: Props) => {
  const intl = useIntl();

  const comment = useFormWatch(field.form, field.at('comment').value.get);
  const isVisible = useFormWatch(field.form, field.at('visible').value.get);

  const [isCommentFormOpen, setCommentFormOpen] = useState(false);
  const [isResetConfirmationOpen, setResetConfirmationOpen] = useState(false);

  const handleAreaVisibilityToggle = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      modifyField(field.at('visible'), (value) => !value),
    ));
  }, [field]);

  const handleCommentFormOpen = useCallback(() => setCommentFormOpen(true), []);
  const handleCommentFormCancel = useCallback(() => setCommentFormOpen(false), []);
  const handleCommentFormSubmit = useCallback((value: string) => {
    field.form.change(pipe(
      field.form.currentState,
      field.at('comment').value.set(value),
    ));

    setCommentFormOpen(false);
  }, [field]);

  const handleResetConfirmationOpen = useCallback(() => setResetConfirmationOpen(true), []);
  const handleResetConfirmationCancel = useCallback(() => setResetConfirmationOpen(false), []);
  const handleResetConfirmationSubmit = useCallback(() => {
    const defaultSections = config[group][area].map(getDefaultFormSection);
    field.form.change(pipe(
      field.form.currentState,
      modifyField(field.at('sections'), () => defaultSections),
    ));

    setResetConfirmationOpen(false);
  }, [field, config, group, area]);

  return (
    <div className="bp-hvac-form-group-area">
      <div className="bp-hvac-form-group-area__title">
        <div className="bp-hvac-form-group-area__icon">
          {AREA_ICONS[area]}
        </div>

        <BodyMedium01>{AREA_NAMES[area]}</BodyMedium01>

        {!readOnly && (
          <div className="bp-hvac-form-group-area__title-actions">
            <Tooltip
              id={`bp-hvac-form-reset-${group}-${area}`}
              value={(
                <div className="bp-hvac-form-group-area__tooltip-value">
                  {intl.formatMessage({ id: 'hvac/form/area/actions/reset' })}
                </div>
              )}
            >
              <Button
                className="bp-hvac-form-group-area__button"
                type="button"
                kind="ghost"
                onClick={handleResetConfirmationOpen}
                iconOnly={true}
                icon={(
                  <div className="bp-hvac-form-group-area__icon">
                    <ResetIcon/>
                  </div>
                )}
                size="sm"
              />
            </Tooltip>

            <Tooltip
              id={`bp-hvac-form-visibility-${group}-${area}`}
              value={(
                <div className="bp-hvac-form-group-area__tooltip-value">
                  {isVisible
                    ? <FormattedMessage id="hvac/form/area/actions/hide"/>
                    : <FormattedMessage id="hvac/form/area/actions/show"/>}
                </div>
              )}
            >
              <Button
                className="bp-hvac-form-group-area__button"
                type="button"
                kind="ghost"
                onClick={handleAreaVisibilityToggle}
                iconOnly={true}
                icon={(
                  <div className="bp-hvac-form-group-area__icon">
                    {isVisible ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                  </div>
                )}
                size="sm"
              />
            </Tooltip>
          </div>
        )}
      </div>
      <div className="bp-hvac-form-group-area__form">
        {config[group][area].map((_, index) => (
          <HvacFormSection
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            path={field.at('sections').at(index)}
            opened={openedSection?.path === field.at('sections').at(index).path}
            readOnly={readOnly}
            onToggle={(opened) => setOpenedSection(opened ? field.at('sections').at(index) : null)}
          />
        ))}
      </div>

      <HvacFormAreaResetConfirmationModal
        isOpen={isResetConfirmationOpen}
        onClose={handleResetConfirmationCancel}
        onSubmit={handleResetConfirmationSubmit}
      />

      <HvacFormAreaCommentFormModal
        comment={comment}
        onSubmit={handleCommentFormSubmit}
        isOpen={isCommentFormOpen}
        onClose={handleCommentFormCancel}
      />
    </div>
  );
});

const AREA_ICONS = {
  [HvacArea.HEATING]: <HeatingIcon/>,
  [HvacArea.COOLING]: <CoolingIcon/>,
  [HvacArea.VENTILATION]: <FanIcon/>,
};
const AREA_NAMES = {
  [HvacArea.HEATING]: <FormattedMessage id="hvac/tab/heating"/>,
  [HvacArea.COOLING]: <FormattedMessage id="hvac/tab/cooling"/>,
  [HvacArea.VENTILATION]: <FormattedMessage id="hvac/tab/ventilation"/>,
};
